import { untilDestroyed } from '@ngneat/until-destroy';
import { Observable, take, timeout } from 'rxjs';
import { from } from 'rxjs/internal/observable/from';
import { DeviceState, DeviceErrorType } from '../../models/device-manger';

export enum DeviceType {
  VIDEO = 'video',
  AUDIO = 'audio',
}

export const DEFAULT_DEVICE_ID = 'default';
export const TIMEOUT_GET_USER_MEDIA_CALL = 15000;
export class DevicesHandleUtil {
  static readonly EXACT_DEVICE_SKIPPED_ERRORS = [
    DeviceErrorType.PERMISSION_DENIED,
    DeviceErrorType.PERMISSION_DENIED_BY_SYSTEM,
    DeviceErrorType.NO_INPUT_DETECTED,
  ];

  static onGettingStream(device: DeviceState) {
    device.isGettingStream = true;
    device.state = false;
    device.isToggable = false;
  }
  static onGettingStreamSuccessed(device: DeviceState, notChangeCurrentState = false) {
    if (!notChangeCurrentState) {
      device.state = true;
    }
    device.hasError = false;
    device.errorType = undefined;
    device.actualError = undefined;
  }
  static onGettingStreamFinished(device: DeviceState) {
    device.isGettingStream = false;
    device.isToggable = true;
  }
  static handleDevicesError(deviceState: DeviceState, error: any) {
    this.setDeviceStateError(deviceState);

    deviceState.errorType = this.getErrorTypeFromErrorName(error);
    try {
      deviceState.actualError = String(error);
    } catch (_) {
      deviceState.actualError = 'Unknown error';
    }
  }

  static getErrorTypeFromErrorName(error: any): DeviceErrorType {
    switch (error.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        if (this.isDeniedBySystem(error.toString())) {
          return DeviceErrorType.PERMISSION_DENIED_BY_SYSTEM;
        } else {
          return DeviceErrorType.PERMISSION_DENIED;
        }
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return DeviceErrorType.NOT_FOUND;
      case 'NotReadableError':
      case 'TrackStartError':
        return DeviceErrorType.NO_INPUT_DETECTED;
      case 'TimeoutError':
        return DeviceErrorType.GET_USER_MEDIA_TIMEOUT;
      default:
        return DeviceErrorType.DEFAULT_ERROR;
    }
  }

  private static setDeviceStateError(deviceState: DeviceState) {
    deviceState.state = false;
    deviceState.isToggable = false;
    deviceState.hasError = true;
  }
  static async gUMInputDevice(
    context: any,
    deviceType: DeviceType,
    deviceId: string | undefined,
    deviceState: DeviceState,
    preferredConstraints?: MediaTrackConstraints,
    exactDeviceId = true,
  ): Promise<MediaStream> {
    const gUMConstraints: MediaStreamConstraints = this.constructGUMConstraints(
      deviceType,
      deviceId ?? DEFAULT_DEVICE_ID,
      exactDeviceId,
      preferredConstraints,
    );
    try {
      const mediaStream = await this.createUserMediaPromise(
        context,
        gUMConstraints,
        deviceType,
        deviceState,
      );
      return mediaStream;
    } catch (error) {
      const errorType = this.getErrorTypeFromErrorName(error);
      if (exactDeviceId && !this.EXACT_DEVICE_SKIPPED_ERRORS.includes(errorType)) {
        return this.gUMInputDevice(
          context,
          deviceType,
          deviceId,
          deviceState,
          preferredConstraints,
          false,
        );
      }
      throw error;
    }
  }

  private static createUserMediaPromise(
    context: any,
    gUMConstraints: MediaStreamConstraints,
    deviceType: DeviceType,
    deviceState: DeviceState,
  ): Promise<MediaStream> {
    return new Promise<MediaStream>((resolve, reject) => {
      DevicesHandleUtil.onGettingStream(deviceState);
      const gUMObservable = from(navigator.mediaDevices.getUserMedia(gUMConstraints)).pipe(
        timeout(TIMEOUT_GET_USER_MEDIA_CALL),
        take(1),
        untilDestroyed(context),
      );
      this.subscribeToGUMObservable(gUMObservable, resolve, reject, deviceType, deviceState);
    });
  }

  private static subscribeToGUMObservable(
    gUMObservable: Observable<MediaStream>,
    resolve: (value: MediaStream | PromiseLike<MediaStream>) => void,
    reject: { (reason?: any): void; (arg0: null): void },
    deviceType: DeviceType,
    deviceState: DeviceState,
  ) {
    gUMObservable.subscribe({
      next: async (stream) => {
        // If the device input is an audio device
        // Or just using this function to check devices status
        DevicesHandleUtil.onGettingStreamSuccessed(deviceState);
        DevicesHandleUtil.onGettingStreamFinished(deviceState);
        resolve(stream);
      },
      error: async (deviceError) => {
        // If timeout is detected, we will skip it if the permission status is still "prompt" which means the top left popup is still opened
        if (deviceError.name === 'TimeoutError' && this.browserSupportsPermissionsQuery()) {
          const permissionIsPrompt = await this.isDevicePermissionStatusPrompt(deviceType);
          if (permissionIsPrompt) {
            this.subscribeToGUMObservable(gUMObservable, resolve, reject, deviceType, deviceState);
            return;
          }
        }
        DevicesHandleUtil.handleDevicesError(deviceState, deviceError);
        DevicesHandleUtil.onGettingStreamFinished(deviceState);
        reject(deviceError);
      },
    });
  }

  public static async isDevicePermissionStatusPrompt(deviceType: DeviceType) {
    try {
      if (!this.browserSupportsPermissionsQuery()) {
        return false;
      }
      const permissionStatus = await navigator.permissions.query({
        name: (deviceType === DeviceType.AUDIO ? 'microphone' : 'camera') as PermissionName,
      });
      return permissionStatus.state === 'prompt';
    } catch (e) {
      return false;
    }
  }

  private static constructGUMConstraints(
    deviceType: DeviceType,
    deviceId: string,
    exactDeviceId: boolean,
    preferredConstraints?: MediaTrackConstraints,
  ): any {
    const gUMConstraints: MediaTrackConstraints = {
      deviceId: exactDeviceId ? { exact: deviceId } : { ideal: deviceId },
    };

    if (!preferredConstraints) {
      return { [deviceType]: gUMConstraints };
    }

    return { [deviceType]: { ...gUMConstraints, ...preferredConstraints } };
  }

  private static browserSupportsPermissionsQuery() {
    return 'permissions' in navigator && 'query' in (navigator as any).permissions;
  }

  static isDeniedBySystem(error: string): boolean {
    return error.toLocaleLowerCase().includes('permission denied by system');
  }
}
